import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";

export interface ThinkingBlocksContent {
  eyebrow: string;
  headline: string;
  description: string;
  insights: {
    title: string;
    points: string[];
    note: string;
  }[];
}

export const thinkingBlocksDefaults: ThinkingBlocksContent = {
  eyebrow: "Our Thinking",
  headline: "Experience compounds into better decisions.",
  description: "Frameworks developed from hundreds of growth engagements. Not theory. Pattern recognition.",
  insights: [
    {
      title: "What we optimize first vs. later",
      points: [
        "First: Conversion efficiency. Before scaling spend.",
        "Then: Channel diversification. After unit economics work.",
        "Last: Brand investment. Once performance is predictable.",
      ],
      note: "Order matters. Most brands invert this sequence.",
    },
    {
      title: "How growth breaks at different stages",
      points: [
        "0→1: Targeting too broad, messaging too vague",
        "1→10: Attribution gaps hide what's actually working",
        "10→100: Infrastructure can't support decision velocity",
      ],
      note: "We've seen these patterns across 200+ engagements.",
    },
    {
      title: "Common scaling mistakes we avoid",
      points: [
        "Scaling channels before conversion is efficient",
        "Treating retention as an afterthought",
        "Building dashboards instead of decision frameworks",
      ],
      note: "Mistakes that look like growth until they don't.",
    },
  ],
};

interface ThinkingBlocksProps {
  content?: ThinkingBlocksContent;
}

const ThinkingBlocks = ({ content: c = thinkingBlocksDefaults }: ThinkingBlocksProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border relative overflow-hidden">
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 90% 60% at 90% 30%, hsl(169 66% 52% / 0.03) 0%, transparent 50%)`,
        }}
      />
      
      <div className="container-wide relative z-10">
        <motion.div 
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-5">
            {c.headline}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg">
            {c.description}
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-px bg-border"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.insights.map((insight) => (
            <motion.div
              key={insight.title}
              variants={itemVariant}
              className="bg-background p-6 md:p-8 lg:p-12 transition-colors duration-150 hover:bg-muted/20"
            >
              <h3 className="text-base font-medium text-foreground mb-6">{insight.title}</h3>
              <ul className="space-y-3.5 mb-8">
                {insight.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                    <span className="w-1 h-1 rounded-full bg-primary mt-2 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground/60 italic border-t border-border pt-6">
                {insight.note}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ThinkingBlocks;
